import dayjs from "dayjs";
import DOMPurify from "dompurify";
import { ChevronDown, SquarePen, Trash2 } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { Button } from "@/app/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/app/components/ui/dialog";
import { cn } from "@/app/handlers/cn.utils";

/** Height the rich-text body collapses to, in px — roughly four lines of `text-sm`. */
const COLLAPSED_BODY_HEIGHT = 84;

/**
 * The value transforms stringify missing parts, so a blank city arrives as the
 * literal "undefined" and a blank country as "null". Both used to be printed.
 */
const PLACEHOLDER_VALUES = new Set(["undefined", "null", "NaN"]);

/** Drops the empty and stringified-empty parts, so no card shows a stray ", ". */
export function joinMeta(parts: (string | number | undefined | null)[], separator = ", "): string {
	return parts
		.map((part) => (part === undefined || part === null ? "" : String(part).trim()))
		.filter((part) => part !== "" && !PLACEHOLDER_VALUES.has(part))
		.join(separator);
}

/**
 * One location line from its parts. A region equal to the city is the copy a save
 * writes when only one of the two was given — the backend calls a section complete
 * only when city, region and country are all filled — so it is dropped here rather
 * than printed as "Nairobi, Nairobi".
 */
export function joinLocation(
	city?: string | null,
	region?: string | null,
	country?: string | null,
	separator = ", ",
): string {
	return joinMeta([city, String(region ?? "").trim() === String(city ?? "").trim() ? "" : region, country], separator);
}

/**
 * `dayjs("").format(...)` returns the string "Invalid Date", which the summary
 * cards printed verbatim whenever a date was missing. An empty string instead
 * lets `joinMeta` drop the whole segment.
 */
export function formatMonthYear(date: string | number | Date | undefined | null): string {
	if (date === undefined || date === null || date === "") return "";
	const parsed = dayjs(date);
	return parsed.isValid() ? parsed.format("MMM YYYY") : "";
}

/**
 * True when the rich-text HTML carries something a reader can see. The editor
 * saves `<p><br></p>` for an emptied field, which is not an empty string but
 * renders as blank space — enough to open a gap under every meta line.
 */
function hasVisibleContent(html: string | undefined): boolean {
	if (!html) return false;
	return (
		html
			.replace(/<[^>]*>/g, "")
			.replace(/&nbsp;/g, " ")
			.trim().length > 0
	);
}

interface SummaryCardProps {
	/** Section glyph, shown in the leading chip. */
	icon: ReactNode;
	/** The most specific thing about the entry: field of study, or job title. */
	title: string;
	/** Where it happened: the school, or the employer. */
	subtitle?: string;
	/** Short qualifier shown beside the title — degree level, or "Current". */
	badge?: string;
	badgeTone?: "neutral" | "accent";
	/** Location, dates, and anything else secondary. Empty entries are dropped. */
	meta?: (string | undefined)[];
	/** User-authored rich HTML. Sanitized here, never sliced. */
	html?: string;
	onEdit: () => void;
	onDelete: () => void;
	deleteTitle: string;
	deleteDescription: string;
	/** Names the entry for screen readers, so repeated buttons stay distinguishable. */
	entryLabel: string;
}

const SummaryCard = ({
	icon,
	title,
	subtitle,
	badge,
	badgeTone = "neutral",
	meta = [],
	html,
	onEdit,
	onDelete,
	deleteTitle,
	deleteDescription,
	entryLabel,
}: SummaryCardProps) => {
	const [showDeleteWarning, setShowDeleteWarning] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [overflows, setOverflows] = useState(false);
	const bodyRef = useRef<HTMLDivElement>(null);

	const metaLine = joinMeta(meta, " • ");
	const showBody = hasVisibleContent(html);

	useEffect(() => {
		const node = bodyRef.current;
		if (!node) return;

		// Measured on the inner, unclamped element: the outer wrapper is capped at
		// COLLAPSED_BODY_HEIGHT, so its own box never changes size as content grows
		// and a ResizeObserver on it would never fire. A one-shot measure is not
		// enough either — fonts settle after the first paint and shift the height.
		const observer = new ResizeObserver(() => {
			setOverflows(node.scrollHeight > COLLAPSED_BODY_HEIGHT + 4);
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, []);

	return (
		<>
			<div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 font-inter transition-colors hover:border-gray-300 sm:p-5">
				<div className="flex items-start gap-3 sm:gap-4">
					<div className="min-w-0 flex-1">
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
									{/* Wraps rather than truncates: this screen exists so the reader can
									    check what they saved, and a clipped "Bachelor of Science in Electr…"
									    hides the part they came to verify. */}
									<h3 className="m-0 wrap-break-word font-semibold text-base text-gray-900 leading-tight">{title}</h3>
								</div>
								{subtitle ? <p className="mt-1 wrap-break-word font-medium text-gray-700 text-sm">{subtitle}</p> : null}
								{metaLine ? <p className="mt-0.5 text-gray-500 text-xs">{metaLine}</p> : null}
							</div>

							<div className="flex shrink-0 items-center gap-2">
								{/* `ignore-focus-behavior` opts out of the legacy `button:focus` rule in
								    main.css, which forces `outline: none !important` and repaints the
								    label #414141 — it swallowed the focus ring and greyed the buttons
								    the moment a keyboard reader reached them. */}
								<button
									type="button"
									onClick={onEdit}
									className="ignore-focus-behavior flex min-w-max cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-2 text-gray-700 text-sm leading-none transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-primary-700 focus-visible:outline-offset-2"
								>
									<SquarePen className="size-4 text-gray-500" />
									{/* `hidden` drops the label from the accessibility tree too, so the
									    narrow-screen icon-only button needs its own full name. */}
									<span aria-hidden="true" className="hidden sm:inline">
										Edit
									</span>
									<span className="sr-only">Edit {entryLabel}</span>
								</button>
								<button
									type="button"
									onClick={() => setShowDeleteWarning(true)}
									className="ignore-focus-behavior flex cursor-pointer items-center rounded-lg border border-gray-200 px-2.5 py-2 leading-none transition-colors hover:border-red-200 hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-red-600 focus-visible:outline-offset-2"
								>
									<Trash2 className="size-4 text-red-600" />
									<span className="sr-only">Delete {entryLabel}</span>
								</button>
							</div>
						</div>

						{showBody ? (
							<div className="mt-3">
								<div
									className="relative overflow-hidden"
									style={{
										maxHeight: expanded ? undefined : COLLAPSED_BODY_HEIGHT,
									}}
								>
									<div
										ref={bodyRef}
										// Sanitized whole, never sliced: the old cards cut the raw HTML at
										// a fixed character count, which could land inside a tag and cost
										// the reader the rest of the list once DOMPurify repaired it.
										// Length is handled below, by clamping the rendered height.
										dangerouslySetInnerHTML={{
											__html: DOMPurify.sanitize(html ?? ""),
										}}
										className="box text-gray-600 text-sm leading-relaxed [&_li]:mt-1 [&_p]:mt-1 [&_p:first-child]:mt-0"
									/>
									{!expanded && overflows ? (
										<div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-linear-to-t from-white to-transparent" />
									) : null}
								</div>
								{overflows ? (
									<button
										type="button"
										onClick={() => setExpanded((open) => !open)}
										className="ignore-focus-behavior mt-1.5 flex cursor-pointer items-center gap-1 font-medium text-primary-700 text-sm hover:underline focus-visible:outline-2 focus-visible:outline-primary-700 focus-visible:outline-offset-2"
									>
										{expanded ? "Show less" : "Show more"}
										<ChevronDown className={cn("size-4 transition-transform", expanded && "rotate-180")} />
										<span className="sr-only">of {entryLabel}</span>
									</button>
								) : null}
							</div>
						) : null}
					</div>
				</div>
			</div>

			<Dialog open={showDeleteWarning} onOpenChange={setShowDeleteWarning}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{deleteTitle}</DialogTitle>
						<DialogDescription>{deleteDescription}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type="button" variant="secondary" onClick={() => setShowDeleteWarning(false)}>
							Cancel
						</Button>
						<Button
							type="button"
							onClick={() => {
								onDelete();
								setShowDeleteWarning(false);
							}}
						>
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default SummaryCard;
